import { Component, OnInit, OnDestroy, DestroyRef, inject, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableModule } from 'primeng/table';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { Select } from 'primeng/select';
import { InputText } from 'primeng/inputtext';
import { Tooltip } from 'primeng/tooltip';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { catchError, of } from 'rxjs';
import { Client } from '../../models/client.model';
import { ClientService } from '../../services/client.service';
import { ServiceGroupService } from '../../services/service-group.service';
import { ServiceGroup } from '../../models/service-group.model';
import { Checkbox } from 'primeng/checkbox';
import { AuthService } from '../../services/auth.service';
import { PageHeaderSearchActionService } from '../../services/page-header-search-action.service';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

@Component({
  selector: 'app-client-management',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    Button,
    Dialog,
    Select,
    InputText,
    Tooltip,
    Checkbox,
    ConfirmDialog
  ],
  templateUrl: './client-management.component.html',
  styleUrl: './client-management.component.scss',
  providers: [ConfirmationService]
})
export class ClientManagementComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);
  private ngZone = inject(NgZone);
  clients: Client[] = [];
  filteredClients: Client[] = [];
  selectedClient: Client = this.getEmptyClient();
  displayDialog = false;
  dialogMode: 'add' | 'edit' = 'add';
  loading = false;
  searchText: string = '';
  selectedStatusFilter: string | null = 'active';
  selectedServiceGroupFilter: string | null = null;
  serviceGroups: ServiceGroup[] = [];
  fieldTouched: Record<string, boolean> = {};

  rows: number = 50;
  first: number = 0;
  totalRecords: number = 0;
  rowsPerPageOptions: number[] = [5, 10, 20, 50];

  statusFilterOptions: { label: string; value: string | null }[] = [
    { label: 'All', value: null },
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' }
  ];

  get totalClientsCount(): number { return this.clients.length; }
  get activeClientsCount(): number { return this.clients.filter(c => c.isActive).length; }
  get inactiveClientsCount(): number { return this.clients.filter(c => !c.isActive).length; }

  constructor(
    private clientService: ClientService,
    private serviceGroupService: ServiceGroupService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private authService: AuthService,
    public pageHeaderService: PageHeaderSearchActionService
  ) {}

  ngOnInit() {
    this.pageHeaderService.setCanShowFilterToggle(true);
    this.pageHeaderService.setPlaceholder('Search by title or description...');
    this.pageHeaderService.setPrimaryButtonLabel('Add Client');
    this.pageHeaderService.setCanShowExport(true);
    this.pageHeaderService.setExportDisabled(true);
    this.searchText = this.pageHeaderService.currentSearchText;
    this.loadServiceGroups();
    this.loadClients();
    this.pageHeaderService.searchText.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(v => {
      this.searchText = v ?? '';
      this.applySearch();
    });
    this.pageHeaderService.primaryActionClick.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.showAddDialog());
    this.pageHeaderService.exportClick.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.ngZone.run(() => this.exportToExcel());
    });
    this.authService.user$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(user => {
      this.pageHeaderService.setCanShowPrimaryAction(!!user?.isAdmin);
    });
  }

  ngOnDestroy(): void {
    this.pageHeaderService.clearPageConfig();
  }

  private loadServiceGroups(): void {
    this.serviceGroupService.getAllServiceGroups().subscribe({
      next: (groups) => {
        this.serviceGroups = (groups || []).filter(sg => sg.isActive === true);
      },
      error: () => {
        this.serviceGroups = [];
      }
    });
  }

  loadClients() {
    this.loading = true;
    this.clientService.getAllClients().pipe(
      catchError(error => {
        console.error('Error loading clients:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load clients. Please refresh the page or try again later.'
        });
        this.loading = false;
        // Return empty array to prevent complete failure
        return of([] as Client[]);
      })
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        // Ensure isActive is set for all clients (default to true if missing)
        this.clients = data.map(client => ({
          ...client,
          isActive: client.isActive !== undefined ? client.isActive : true
        }));
        // Reapply filters to preserve filtered state
        this.applySearch();
        this.loading = false;
      }
    });
  }

  onSearch() {
    this.applySearch();
  }

  applySearch() {
    let filtered = [...this.clients];

    // Apply search text filter
    if (this.searchText.trim() !== '') {
      const searchLower = this.searchText.toLowerCase();
      filtered = filtered.filter(client =>
        client.name?.toLowerCase().includes(searchLower) ||
        client.description?.toLowerCase().includes(searchLower)
      );
    }

    // Apply status filter
    if (this.selectedStatusFilter !== null) {
      if (this.selectedStatusFilter === 'active') {
        filtered = filtered.filter(client => client.isActive === true);
      } else if (this.selectedStatusFilter === 'inactive') {
        filtered = filtered.filter(client => client.isActive === false);
      }
    }

    // Apply Service Group filter
    if (this.selectedServiceGroupFilter) {
      filtered = filtered.filter(client => client.serviceGroupId === this.selectedServiceGroupFilter);
    }

    this.filteredClients = filtered;
    this.totalRecords = this.filteredClients.length;
    this.first = 0;
    this.pageHeaderService.setExportDisabled(this.filteredClients.length === 0);
  }

  clearSearch() {
    this.searchText = '';
    this.applySearch();
  }

  onStatusFilterChange() {
    this.applySearch();
  }

  onServiceGroupFilterChange() {
    this.applySearch();
  }

  clearFilters() {
    this.searchText = '';
    this.pageHeaderService.setSearchText('');
    this.selectedStatusFilter = 'active';
    this.selectedServiceGroupFilter = null;
    this.applySearch();
  }

  hasActiveFilters(): boolean {
    return !!(this.searchText.trim() ||
      (this.selectedStatusFilter !== null && this.selectedStatusFilter !== 'active') ||
      this.selectedServiceGroupFilter);
  }

  toggleActiveStatus(client: Client) {
    const activating = !client.isActive;
    this.confirmationService.confirm({
      message: `Are you sure you want to ${activating ? 'activate' : 'deactivate'} "${client.name}"?`,
      header: activating ? 'Confirm Activate' : 'Confirm Deactivate',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: activating ? 'p-button-success' : 'p-button-danger',
      accept: () => {
        const updatedClient = { ...client, isActive: !client.isActive };
        this.clientService.updateClient(client.id, updatedClient).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: `Client ${updatedClient.isActive ? 'activated' : 'deactivated'} successfully`
            });
            this.loadClients();
          },
          error: (error) => {
            console.error('Error updating client status:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to update client status'
            });
          }
        });
      }
    });
  }

  showAddDialog() {
    this.selectedClient = this.getEmptyClient();
    this.fieldTouched = {};
    this.dialogMode = 'add';
    this.displayDialog = true;
  }

  showEditDialog(client: Client) {
    this.selectedClient = { ...client };
    this.fieldTouched = {};
    this.dialogMode = 'edit';
    this.displayDialog = true;
  }

  hideDialog() {
    this.displayDialog = false;
    this.fieldTouched = {};
    this.selectedClient = this.getEmptyClient();
  }

  resetClientForm() {
    this.selectedClient = this.getEmptyClient();
    this.fieldTouched = {};
  }

  exportToExcel(): void {
    if (!this.filteredClients?.length) {
      this.messageService.add({ severity: 'warn', summary: 'No Data', detail: 'No clients to export' });
      return;
    }
    try {
      const exportData = this.filteredClients.map(c => ({
        'Title': c.name || '',
        'Description': (c.description || '').slice(0, 200),
        'Folder': c.folderNumber || '',
        'Service Group': c.serviceGroupName || '',
        'Status': c.isActive ? 'Active' : 'Inactive'
      }));
      const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(exportData);
      worksheet['!cols'] = [{ wch: 22 }, { wch: 36 }, { wch: 10 }, { wch: 18 }, { wch: 10 }];
      const workbook: XLSX.WorkBook = { Sheets: { 'Clients': worksheet }, SheetNames: ['Clients'] };
      const excelBuffer: unknown = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer as BlobPart], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(data, `Clients_${this.formatDateForFile(new Date())}.xlsx`);
      this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Clients exported to Excel successfully' });
    } catch (error) {
      console.error('Error exporting clients:', error);
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to export clients to Excel' });
    }
  }

  private formatDateForFile(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}${m}${d}`;
  }

  saveClient() {
    this.fieldTouched['name'] = true;
    if (!this.selectedClient.name.trim()) {
      return;
    }

    if (this.dialogMode === 'add') {
      this.clientService.addClient(this.selectedClient).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Client added successfully'
          });
          this.loadClients();
          this.hideDialog();
        },
        error: (error) => {
          console.error('Error adding client:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to add client'
          });
        }
      });
    } else {
      this.clientService.updateClient(this.selectedClient.id, this.selectedClient).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Client updated successfully'
          });
          this.loadClients();
          this.hideDialog();
        },
        error: (error) => {
          console.error('Error updating client:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to update client'
          });
        }
      });
    }
  }

  private getEmptyClient(): Client {
    return {
      id: '',
      name: '',
      description: '',
      folderNumber: null,
      isActive: true,
      serviceGroupId: null,
      serviceGroupName: null,
      disableMsaNotifications: false
    };
  }

  onServiceGroupChange(): void {
    if (this.selectedClient.serviceGroupId) {
      const serviceGroup = this.serviceGroups.find(sg => sg.id === this.selectedClient.serviceGroupId);
      this.selectedClient.serviceGroupName = serviceGroup?.name || null;
    } else {
      this.selectedClient.serviceGroupName = null;
    }
  }
}

